import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../Icon/Icon';
import Select from 'react-select';
import CommonButton from '../../../Buttons/CommonButton';
import { formatNumber } from '../../../../utils/functions';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Hint from '../../../Tools/Hint';
import { updatePricing } from '../../../../services/pricingService';
import { notificationsActions } from '../../../../store';
import Modal from '../../../Modal/Modal';
import ModalHeader from '../../../Modal/ModalHeader/ModalHeader';
import ModalBody from '../../../Modal/ModalBody/ModalBody';
import ModalFooter from '../../../Modal/ModalFooter/ModalFooter';

const UpdatePricing = ({
    isActive,
    screenPrices,
    currenciesWithLabel,
    selectedCurrency,
    selectedTier,
    handleChangeCurrency,
    isUpdateModalActive,
    setIsUpdateModalActive,
    tierList,
    setTierList,
    venueTypes = [],
    onTierLogCreated,
}) => {
    const { t } = useTranslation();

    const dispatch = useDispatch();
    const [pricing, setPricing] = useState('');
    const [selectedVenueType, setSelectedVenueType] = useState(null);
    const [additionalNotes, setAdditionalNotes] = useState('');

    const venueTypeOptions = useMemo(
        () =>
            (venueTypes || []).map((venue) => ({
                value: venue.id,
                label: venue.name,
                description: venue.description,
            })),
        [venueTypes]
    );

    const formatVenueTypeOption = (option) => {
        if (!option) return null;
        return (
            <div className="flex flex-col">
                <span className="text-sm font-medium text-gray-900">
                    {option.label}
                </span>
                {option.description && (
                    <span className="text-xs text-gray-500">
                        {option.description}
                    </span>
                )}
            </div>
        );
    };

    useEffect(() => {
        setPricing(selectedTier?.baseCpm);
        if (!selectedTier) {
            setSelectedVenueType(null);
        } else {
            const normalizedScreenType = (selectedTier?.screenType || '')
                .toString()
                .toLowerCase();
            const match =
                venueTypeOptions.find(
                    (option) =>
                        option.value === selectedTier?.venueTypeId ||
                        option.label.toLowerCase() === normalizedScreenType
                ) || null;
            setSelectedVenueType(match);
        }
        setAdditionalNotes('');
    }, [selectedTier, isUpdateModalActive, venueTypeOptions]);

    const inputPlaceholder = formatNumber('0.00', 'properCurrency', null, {
        currency: 'USD',
    });

    const handleChangePricing = (e) => {
        const price = e.target.value;
        if (price === '') {
            setPricing(price);
            return;
        }

        if (!isNaN(price) && Number(price) >= 0) {
            setPricing(price);
        }
    };

    const handleChangeVenueType = (option) => {
        setSelectedVenueType(option);
    };

    const handleUpdatePrice = () => {
        if (!selectedTier || !selectedVenueType) return;
        const baseCpmValue = parseFloat(pricing);
        const notes = additionalNotes.trim();
        if (Number.isNaN(baseCpmValue)) return;
        const bodyReq = {
            tier: parseInt(selectedTier?.tier, 10),
            screenType: selectedVenueType.label,
            venueTypeId: selectedVenueType.value,
            baseCpm: baseCpmValue,
            notes: notes || undefined,
        };

        updatePricing(selectedTier?.id, bodyReq)
            .then(() => {
                const updatedTierList = tierList.map((item) => {
                    if (item.id === selectedTier.id) {
                        return {
                            ...item,
                            baseCpm: bodyReq.baseCpm,
                            screenType: bodyReq.screenType,
                            venueTypeId: bodyReq.venueTypeId,
                        };
                    }
                    return item;
                });

                setTierList(updatedTierList);
                if (onTierLogCreated) onTierLogCreated();

                dispatch(
                    notificationsActions.setNotification({
                        type: 'success',
                        title: t(
                            'sections.management.section.pricing.updateTier.notificationUpdated.title'
                        ),
                        message: t(
                            'sections.management.section.pricing.updateTier.notificationUpdated.message'
                        ),
                    })
                );

                handleUpdateModal();
            })
            .catch(() => {
                dispatch(
                    notificationsActions.setNotification({
                        type: 'error',
                        title: t(
                            'sections.management.section.pricing.updateTier.error-title'
                        ),
                        message: t(
                            'sections.management.section.pricing.updateTier.error-message'
                        ),
                    })
                );
            });
    };

    const handleUpdateModal = () => {
        setIsUpdateModalActive(!isUpdateModalActive);
    };

    const handleCancelUpdateModal = () => {
        handleUpdateModal();
    };

    return (
        <Modal
            size="sm"
            active={isUpdateModalActive}
            toggler={handleCancelUpdateModal}>
            <ModalHeader toggler={handleCancelUpdateModal}>
                {t('sections.management.section.pricing.updateTier.tab')}:{' '}
                {selectedTier?.tier} -{' '}
                {selectedVenueType?.label || selectedTier?.screenType}
            </ModalHeader>
            <ModalBody>
                <div
                    id="modal-update-body"
                    data-testid="modal-update-body"
                    className="space-y-6 px-4 w-full">
                    <div className="space-y-2">
                        <label
                            htmlFor="update-price"
                            className="block text-gray-700 text-sm font-bold">
                            <span className="flex items-center">
                                <Icon
                                    name="sign"
                                    fixedWidth
                                    className="text-blue-500"
                                />
                                <p className="ml-1">
                                    {t(
                                        'sections.management.section.pricing.updateTier.screenType.title'
                                    )}
                                </p>
                            </span>
                            <p className="font-light">
                                {t(
                                    'sections.management.section.pricing.updateTier.screenType.updateLabel'
                                )}
                            </p>
                        </label>
                        <div className="my-2 w-full">
                            <Select
                                inputId="update-price"
                                value={selectedVenueType}
                                onChange={handleChangeVenueType}
                                options={venueTypeOptions}
                                placeholder={t(
                                    'sections.management.section.pricing.updateTier.screenType.updateLabel'
                                )}
                                noOptionsMessage={() =>
                                    t(
                                        'sections.management.section.pricing.createTier.screenType.empty'
                                    )
                                }
                                formatOptionLabel={formatVenueTypeOption}
                                classNamePrefix="update-tier-select"
                            />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <label
                            htmlFor="update-tier-notes"
                            className="block text-gray-700 text-sm font-bold">
                            <span className="flex items-center">
                                <Icon
                                    name="comment-dots"
                                    fixedWidth
                                    className="text-blue-500"
                                />
                                <p className="ml-1">
                                    {t(
                                        'sections.management.section.pricing.updateTier.notes.title'
                                    )}
                                </p>
                                <span className="ml-2 text-xs font-normal text-gray-400">
                                    {t('common.labels.optional', {
                                        defaultValue: 'Optional',
                                    })}
                                </span>
                            </span>
                            <p className="font-light">
                                {t(
                                    'sections.management.section.pricing.updateTier.notes.label'
                                )}
                            </p>
                        </label>
                        <div className="my-2 w-full">
                            <textarea
                                id="update-tier-notes"
                                value={additionalNotes}
                                onChange={(event) =>
                                    setAdditionalNotes(event.target.value)
                                }
                                className="focus:ring-0 focus:outline-none block w-full bottom-0 border-gray-300 rounded-md p-3"
                                rows={3}
                                placeholder={t(
                                    'sections.management.section.pricing.updateTier.notes.placeholder'
                                )}
                            />
                        </div>
                    </div>
                    <div className={`block w-full bg-opacity-90 `}>
                        <label
                            htmlFor="price-input"
                            className="block text-gray-700 text-sm font-bold">
                            <span className="flex items-center">
                                <Icon
                                    name="coin"
                                    fixedWidth
                                    className="text-blue-500"
                                />
                                <p className="ml-1">
                                    {t(
                                        'sections.management.section.pricing.updateTier.setPrice.title'
                                    )}
                                </p>
                                <Hint
                                    className="ml-1 font-normal"
                                    title={t(
                                        'sections.management.section.pricing.common.hint.title'
                                    )}
                                    text={t(
                                        'sections.management.section.pricing.common.hint.description'
                                    )}
                                    type={'info'}
                                    dataFor="price"
                                />
                            </span>
                            <p className="font-light">
                                {t(
                                    'sections.management.section.pricing.updateTier.setPrice.label'
                                )}
                            </p>
                        </label>
                        <div className="my-2 w-full">
                            <input
                                type="number"
                                steps="0.01"
                                min="0"
                                name="price"
                                id="price-input"
                                data-testid="price-input"
                                value={pricing}
                                autoComplete="off"
                                onChange={handleChangePricing}
                                className="focus:ring-2 focus:ring-blue-200 focus:outline-none block w-full border border-gray-200 rounded-lg px-3 py-2"
                                placeholder={inputPlaceholder}
                            />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <label
                            htmlFor="tier"
                            className="block text-gray-700 text-sm font-bold mb-2">
                            <Icon
                                name="coin"
                                fixedWidth
                                className="text-blue-500"
                            />{' '}
                            {t(
                                'sections.management.section.pricing.common.currency.title'
                            )}
                            <p className="font-light">
                                {t(
                                    'sections.management.section.pricing.common.currency.label'
                                )}
                            </p>
                        </label>
                        <div className="my-2 w-full">
                            <Select
                                inputId="currency"
                                id="select-currency"
                                placeholder={t(
                                    'sections.management.section.billing.funding.currencySelect'
                                )}
                                options={currenciesWithLabel}
                                value={selectedCurrency}
                                onChange={handleChangeCurrency}
                                classNamePrefix="update-tier-select"
                            />
                        </div>
                        {selectedCurrency && pricing && screenPrices && (
                            <div className="rounded-lg bg-blue-50 text-blue-700 px-4 py-3 text-sm">
                                {t(
                                    'sections.management.section.pricing.common.price.title'
                                )}{' '}
                                {selectedCurrency?.value}:{' '}
                                {formatNumber(
                                    pricing *
                                        (screenPrices[
                                            selectedCurrency?.value
                                        ] || 1),
                                    'properCurrency',
                                    null,
                                    {
                                        currency: selectedCurrency?.value,
                                    }
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <CommonButton
                    onClick={handleCancelUpdateModal}
                    label={t('common.buttons.cancel')}
                    styleType={'danger'}
                    className="mx-2 capitalize "
                    id="modal-update-cancel"
                />
                <CommonButton
                    disabled={
                        !selectedTier ||
                        pricing === '' ||
                        !selectedVenueType
                    }
                    label={t(
                        'sections.management.section.pricing.updateTier.tab'
                    )}
                    styleType="primary"
                    className="mx-2 capitalize "
                    onClick={handleUpdatePrice}
                    id="modal-update-save"
                />
            </ModalFooter>
        </Modal>
    );
};

export default UpdatePricing;
